import moment from "moment";
import { handleErrorMessage } from "helpers/methods";
import {
  getReportEnvelopesMetrics,
  getTotalDocumentProgressMetrics,
} from "./InsightsServices";
import { getRequestStatusCountMetrics } from "screens/Reports/services/ReportsService";

export const fetchEnvelopeGraphData = async (
  fromDate: string,
  toDate: string,
  metrics: string[],
  setIsLoading: (loading: boolean) => void,
  setChartData: (data: any[]) => void,
) => {
  try {
    setIsLoading(true);
    const payload = {
      metricList: metrics,
      dateRange: {
        fromDate: fromDate,
        toDate: toDate,
      },
    };

    const response = await getReportEnvelopesMetrics(payload);

    if (response && response.data) {
      const responseByDate = new Map<string, any>();
      response.data.forEach((item: any) => {
        const normalizedDate = moment(item.date, "MM/DD/YYYY").format(
          "YYYY-MM-DD",
        );
        responseByDate.set(normalizedDate, item);
      });

      const formattedData: any[] = [];
      const start = moment(fromDate, "YYYY-MM-DD");
      const end = moment(toDate, "YYYY-MM-DD");
      const cursor = start.clone();

      while (cursor.isSameOrBefore(end, "day")) {
        const key = cursor.format("YYYY-MM-DD");
        const item = responseByDate.get(key);
        formattedData.push({
          displayDate: cursor.format("D MMM"),
          originalDate: cursor.format("MM/DD/YYYY"),
          Requested: item?.requested || 0,
          Sent: item?.sent || 0,
          Opened: item?.opened || 0,
          Completed: item?.completed || 0,
        });
        cursor.add(1, "day");
      }

      setChartData(formattedData);
    }
  } catch (error: any) {
    handleErrorMessage(error);
  } finally {
    setIsLoading(false);
  }
};

export const fetchTotalProgressData = async (
  fromDate: string,
  toDate: string,
  setTotalProgressData: (data: any) => void,
) => {
  const getFallbackProgressData = () => ({
    counts: { requested: 0, sent: 0, opened: 0, completed: 0 },
    maxCount: 1,
    percentages: { sentPer: 0, openedPer: 0, completedPer: 0 },
    closeRate: 0,
  });

  try {
    const payload = {
      dateRange: { fromDate: fromDate, toDate: toDate },
    };
    const response = await getTotalDocumentProgressMetrics(payload);
    if (response) {
      const source = response?.data ?? response ?? {};
      const requested = source.requested || 0;
      const sent = source.sent || 0;
      const opened = source.opened || 0;
      const completed = source.completed || 0;
      //   percentage
      const calcPercentage = (val: number, total: number) => {
        return total > 0 ? Number(((val / total) * 100).toFixed(2)) : 0;
      };
      const maxCount = Math.max(requested, sent, opened, completed, 1);
      const formattedDate = {
        counts: { requested, sent, opened, completed },
        maxCount: maxCount,
        percentages: {
          sentPer: calcPercentage(sent, requested),
          openedPer: calcPercentage(opened, sent),
          completedPer: calcPercentage(completed, opened),
        },
        closeRate: calcPercentage(completed, sent),
      };
      setTotalProgressData(formattedDate);
    } else {
      setTotalProgressData(getFallbackProgressData());
    }
  } catch (error: any) {
    handleErrorMessage(error);
    setTotalProgressData(getFallbackProgressData());
  }
};

export const fetchRequestStatusPieData = async (
  fromDate: string,
  toDate: string,
  metricList: string[],
  setStatusData: (data: any[]) => void,
) => {
  try {
    const payload = {
      metricList,
      dateRange: {
        fromDate,
        toDate,
      },
    };

    const response = await getRequestStatusCountMetrics(payload);
    const source = response?.data ?? response ?? {};

    const data = metricList.map((metric) => {
      const exact = source?.[metric];
      const lower = source?.[metric.toLowerCase()];
      const value = exact ?? lower ?? 0;
      return {
        name: metric,
        value: typeof value === "number" ? value : Number(value) || 0,
      };
    });

    setStatusData(data);
  } catch (error: any) {
    handleErrorMessage(error);
    setStatusData([]);
  }
};
